// Optional analytics consent. Basic consent mode: gtag.js is only ever fetched
// once someone has chosen "Allow analytics", so declining means nothing at all
// is sent to Google - no events, no cookieless pings, no cookies.
//
// The decision lives on the browser, because that is what it governs. It is
// mirrored onto the account so a decline can follow the member to their next
// device; an allow never travels, so analytics can only ever start where
// someone has said yes on that device.
import { getToken, setAnalyticsConsentOnAccount } from './api';

const MEASUREMENT_ID = 'G-H7RY77593G';
const DISABLE_FLAG = `ga-disable-${MEASUREMENT_ID}`;
const STORAGE_KEY = 'irecommend-analytics-consent';
const GRANTED = 'granted';
const DENIED = 'denied';
// Lets a mounted prompt hide itself when the choice is made from Settings instead.
export const CONSENT_EVENT = 'irecommend-analytics-consent-change';

// null means nobody has been asked yet, which is what shows the prompt.
export const readConsent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === GRANTED || stored === DENIED ? stored : null;
  } catch {
    return null;
  }
};

const store = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* private mode: the choice lasts for this session and defaults back to denied */
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
};

// The decision is also kept on the account, so a member who has declined is not
// asked again on their next device. Failing to record it is deliberately silent:
// the local choice already stands, and the only direction this call can fail in
// is "not remembered elsewhere", never "analytics switched on".
const rememberOnAccount = (value) => {
  if (!getToken()) return;
  setAnalyticsConsentOnAccount(value).catch(() => {});
};

// Adopt the account's decision on a browser that has none of its own. Only ever
// called with a payload from a verified session, never from the cached user.
//
// A decline is adopted, because honouring "no thanks" everywhere is what the
// member meant. An allow is NOT: consent covers storage on the device in front
// of them, so analytics stays off here and they are asked once on this browser.
export const adoptAccountConsent = (accountConsent) => {
  if (accountConsent !== DENIED) return readConsent();
  if (readConsent() !== null) return readConsent();
  denyAnalytics({ remember: false });
  return DENIED;
};

const loadGtag = () => {
  const src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const tag = document.createElement('script');
  tag.async = true;
  tag.src = src;
  document.head.appendChild(tag);
};

// Every GA4 cookie this app can reach: the client id and the per-property state.
// They are not httpOnly, so withdrawing consent can genuinely clear them.
const clearGaCookies = () => {
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));
  if (!names.length) return;

  // GA writes to the highest domain it can, so expire each candidate parent.
  const parts = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i += 1) {
    const domain = parts.slice(i).join('.');
    domains.push(domain, `.${domain}`);
  }

  names.forEach((name) => {
    domains.forEach((domain) => {
      const scope = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${scope}`;
    });
  });
};

// Belt and braces for the recovery screen: the consent sheet is already
// suppressed there, so this cannot normally be reached, but analytics must never
// start up on a page that has held a reset token.
const RECOVERY_PATH = '/reset-password';
const onRecoveryScreen = () => window.location.pathname === RECOVERY_PATH;

export const grantAnalytics = () => {
  if (onRecoveryScreen()) return;
  store(GRANTED);
  rememberOnAccount(GRANTED);
  window[DISABLE_FLAG] = false;
  if (typeof window.gtag !== 'function') return;
  // Analytics only. The advertising signals stay denied either way.
  window.gtag('consent', 'update', { analytics_storage: GRANTED });
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID);
  loadGtag();
};

export const denyAnalytics = ({ remember = true } = {}) => {
  store(DENIED);
  if (remember) rememberOnAccount(DENIED);
  // Google's own kill switch, set first. A consent update alone would leave an
  // already-loaded library free to keep sending cookieless pings on unload.
  window[DISABLE_FLAG] = true;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: DENIED });
  }
  clearGaCookies();
};

export const setAnalyticsConsent = (value) =>
  value === GRANTED ? grantAnalytics() : denyAnalytics();